// What the browser will tell us about the device without asking for any
// permission. It travels with the spin wheel requests so the server can note
// which device each check-in, spin and prize came from. Users are anonymous:
// nothing here identifies a person, it only describes the device in use.

const MAX_LANGUAGES = 10;

const numberOrUndefined = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

const stringOrUndefined = (value) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

const booleanOrUndefined = (value) => (typeof value === 'boolean' ? value : undefined);

const dropEmpty = (fields) => Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined));

const readTimezone = () => {
  try {
    return stringOrUndefined(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return undefined;
  }
};

// How the app was opened (a stored "source" such as a QR code or a link), when
// the app recorded one.
const readSource = () => {
  try {
    return stringOrUndefined(localStorage.getItem('source'));
  } catch {
    return undefined;
  }
};

// Installed to the home screen or opened in a browser tab.
const readStandalone = (nav) => {
  try {
    if (typeof nav.standalone === 'boolean') {
      return nav.standalone;
    }

    return booleanOrUndefined(window.matchMedia?.('(display-mode: standalone)')?.matches);
  } catch {
    return undefined;
  }
};

const readLanguages = (nav) =>
  Array.isArray(nav.languages) && nav.languages.length > 0
    ? nav.languages.slice(0, MAX_LANGUAGES).map(stringOrUndefined).filter(Boolean)
    : undefined;

export const collectDeviceFingerprint = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }

  const nav = navigator;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  const screen = window.screen || {};

  return dropEmpty({
    timezone: readTimezone(),
    timezone_offset_minutes: new Date().getTimezoneOffset(),
    language: stringOrUndefined(nav.language),
    languages: readLanguages(nav),
    platform: stringOrUndefined(nav.userAgentData?.platform || nav.platform),
    source: readSource(),
    screen_width: numberOrUndefined(screen.width),
    screen_height: numberOrUndefined(screen.height),
    pixel_ratio: numberOrUndefined(window.devicePixelRatio),
    viewport_width: numberOrUndefined(window.innerWidth),
    viewport_height: numberOrUndefined(window.innerHeight),
    hardware_concurrency: numberOrUndefined(nav.hardwareConcurrency),
    device_memory: numberOrUndefined(nav.deviceMemory),
    max_touch_points: numberOrUndefined(nav.maxTouchPoints),
    connection_type: stringOrUndefined(connection?.type),
    effective_connection_type: stringOrUndefined(connection?.effectiveType),
    downlink: numberOrUndefined(connection?.downlink),
    rtt: numberOrUndefined(connection?.rtt),
    save_data: booleanOrUndefined(connection?.saveData),
    cookies_enabled: booleanOrUndefined(nav.cookieEnabled),
    standalone: readStandalone(nav),
  });
};

export default collectDeviceFingerprint;
